import React, { useEffect, useMemo } from 'react';
import { Text, Radio, Checkbox } from '@perch-labs/shrike';
import {
  FormField,
  FieldOption,
  FieldDependencyConfig,
} from '../../configs/types';
import {
  FieldContainer,
  FieldLabel,
  FieldDescription,
  OptionsContainer,
  OptionLabel,
  DisclaimerContainer,
  DisclaimerText,
} from './qualification-fields.style';

interface QualificationFieldsProps {
  field: FormField;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  interpolations?: Record<string, string>;
  formData?: Record<string, string | string[]>;
}

const checkEnableWhen = (
  enableWhen: FieldDependencyConfig | undefined,
  formData: Record<string, string | string[]>
): boolean => {
  if (!enableWhen) return true;

  const { allOf, anyOf } = enableWhen;

  if (allOf && allOf.length > 0) {
    const allFilled = allOf.every(fieldName => {
      const v = formData[fieldName];
      return Array.isArray(v) ? v.length > 0 : !!v;
    });
    if (!allFilled) return false;
  }

  if (anyOf && anyOf.length > 0) {
    const anyFilled = anyOf.some(fieldName => {
      const v = formData[fieldName];
      return Array.isArray(v) ? v.length > 0 : !!v;
    });
    if (!anyFilled) return false;
  }

  return true;
};

export const QualificationFields = ({
  field,
  value,
  onChange,
  interpolations = {},
  formData = {},
}: QualificationFieldsProps): JSX.Element => {
  const interpolateText = (text: string): string => {
    let result = text;
    Object.entries(interpolations).forEach(([key, val]) => {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), val || '');
    });
    return result;
  };

  const allOptions = useMemo(() => field.options || [], [field.options]);

  const isOptionEnabled = (option: FieldOption): boolean =>
    checkEnableWhen(option.enableWhen, formData);

  // When formData changes, clear any selected option that has become disabled.
  useEffect(() => {
    if (allOptions.length === 0) return;

    if (field.type === 'radio') {
      if (typeof value === 'string' && value) {
        const selected = allOptions.find(o => o.value === value);
        if (selected && !isOptionEnabled(selected)) {
          onChange('');
        }
      }
    } else if (Array.isArray(value) && value.length > 0) {
      const stillEnabled = value.filter(v => {
        const opt = allOptions.find(o => o.value === v);
        return opt ? isOptionEnabled(opt) : false;
      });
      if (stillEnabled.length !== value.length) {
        onChange(stillEnabled);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleRadioChange = (optionValue: string) => {
    onChange(optionValue);
  };

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const currentValues = Array.isArray(value) ? value : [];
    if (checked) {
      onChange([...currentValues, optionValue]);
    } else {
      onChange(currentValues.filter(v => v !== optionValue));
    }
  };

  return (
    <FieldContainer>
      {field.header && <Text textStyle="heading600">{field.header}</Text>}
      {field.description && (
        <FieldDescription>
          {interpolateText(field.description)}
        </FieldDescription>
      )}
      <FieldLabel>{field.label}</FieldLabel>

      <OptionsContainer>
        {allOptions.map(option => {
          const isEnabled = isOptionEnabled(option);
          const interpolatedLabel = interpolateText(option.label);
          const isChecked =
            field.type === 'radio'
              ? value === option.value
              : Array.isArray(value) && value.includes(option.value);

          return (
            <OptionLabel key={option.value} $isEnabled={isEnabled}>
              {field.type === 'radio' ? (
                <Radio
                  name={field.name}
                  value={option.value}
                  checked={isChecked}
                  disabled={!isEnabled}
                  onChange={() => handleRadioChange(option.value)}
                >
                  <>
                    {interpolatedLabel}
                    {!!option.listItems && option.listItems.length > 0 && (
                      <ul
                        style={{
                          marginTop: '8px',
                          paddingLeft: '24px',
                          lineHeight: '150%',
                        }}
                      >
                        {option.listItems.map(item => (
                          <li key={`list-${item}`}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </>
                </Radio>
              ) : (
                <Checkbox
                  name={`${field.name}-${option.value}`}
                  checked={isChecked}
                  disabled={!isEnabled}
                  onChange={e =>
                    handleCheckboxChange(option.value, e.target.checked)
                  }
                >
                  {interpolatedLabel}
                </Checkbox>
              )}
            </OptionLabel>
          );
        })}
      </OptionsContainer>

      {field.disclaimer && field.disclaimer.length > 0 && (
        <DisclaimerContainer>
          {field.disclaimer.map(text => (
            <DisclaimerText
              key={text}
              color="secondary"
              textStyle="paragraph400"
            >
              {text}
            </DisclaimerText>
          ))}
        </DisclaimerContainer>
      )}
    </FieldContainer>
  );
};
